import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { Box, Divider, Stack, Switch, Typography } from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import certificateLogoPreview from "../../../../../../assets/certificate-logo-preview.png";
import favicon from "../../../../../../assets/favicon.jpg";
import landscapeLogo from "../../../../../../assets/landscape-logo.jpg";
import loginLogo from "../../../../../../assets/login-logo.jpg";
import poweredByLogo from "../../../../../../assets/powered-by-logo.jpg";
import powerdByLogoDark from "../../../../../../assets/powerd-by-logo-dark2.jpg";
import squareLogo from "../../../../../../assets/square-logo.jpg";
import { ImageUploader } from "../../../../../../components/ImageUploader/ImageUploader";
import {
  AttributeKey,
  PageUid,
  useCustomisation,
} from "../../../../../../context/CustomisationContext";
import { usePortal } from "../../../../../../context/PortalsContext";
import { useToast } from "../../../../../../context/ToastContext";
import {
  getS3PresignedUrl,
  updateSectionDesign,
} from "../../../../../../services/api/apiClient";
import {
  GetS3PresignedUrlResult,
  PresignFileType,
} from "../../../../../../services/api/types/portalConfiguration/uploadPhoto";
import { brand, gray } from "../../../../../../theme/themePrimitives";
import { setFavicon } from "../../../../../../utils/helpers";

interface LogoCustomisationProps {
  pageId?: PageUid;
}

export const LogoCustomisation: React.FC<LogoCustomisationProps> = ({
  pageId = "GPC",
}) => {
  const { updateCustomisationSection, getCustomisationValue } =
    useCustomisation();
  const { portals } = usePortal();
  const { showToast } = useToast();
  const [loadingStates, setLoadingStates] = useState<{
    Favicon: boolean;
    LandscapeLogo: boolean;
    SquareLogo: boolean;
    PoweredByLogoLight: boolean;
    PoweredByLogoDark: boolean;
  }>({
    Favicon: false,
    LandscapeLogo: false,
    SquareLogo: false,
    PoweredByLogoLight: false,
    PoweredByLogoDark: false,
  });

  const isMainPortal = portals.selectedPortal?.isMain || false;

  const modeOptions = [
    {
      key: "light" as const,
      title: "Light mode",
      description: "Light themed logo",
      iconBg: "#F8FAFC",
      iconColor: gray[700],
      iconText: "Aa",
    },
    {
      key: "dark" as const,
      title: "Dark mode",
      description: "Dark themed logo",
      iconBg: "#101828",
      iconColor: "#FFFFFF",
      iconText: "Aa",
    },
  ];

  const handleAttributeChange = (
    attributeKey: AttributeKey,
    newAttributeValue?: string,
  ) => {
    if (!newAttributeValue) return;

    updateCustomisationSection(pageId, "logoSettings", {
      [attributeKey]: newAttributeValue,
    });

    updateSectionDesign(
      portals.selectedPortal?.portalUid!,
      pageId,
      "logoSettings",
      {
        attributeKey,
        attributeValue: newAttributeValue,
      },
    );
  };

  const handleImageUpload = async (
    resizedFile: File,
    type:
      | "Favicon"
      | "LandscapeLogo"
      | "SquareLogo"
      | "PoweredByLogoLight"
      | "PoweredByLogoDark",
  ) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [type]: true }));
      const response: GetS3PresignedUrlResult = await getS3PresignedUrl(
        portals.selectedPortal?.portalUid!,
        {
          fileName: resizedFile.name,
          contentType: resizedFile.type,
          fileType: PresignFileType.ASSET,
        },
      );
      const { uploadUrl, path } = response;
      await axios.put(uploadUrl, resizedFile, {
        headers: {
          "Content-Type": resizedFile.type,
        },
      });

      const attributeKeyMap: Record<typeof type, AttributeKey> = {
        Favicon: `${pageId.toLowerCase()}Favicon` as AttributeKey,
        LandscapeLogo:
          pageId === "CERTIFICATES"
            ? "certificatesLogo"
            : (`${pageId.toLowerCase()}LandscapeLogo` as AttributeKey),
        SquareLogo: `${pageId.toLowerCase()}SquareLogo` as AttributeKey,
        PoweredByLogoLight: "loginPoweredByLogoLight",
        PoweredByLogoDark: "loginPoweredByLogoDark",
      };

      handleAttributeChange(attributeKeyMap[type], path);
      if (type === "Favicon") {
        setFavicon(path);
      }
    } catch (error: any) {
      console.log("Error:", error);
      showToast(error.response.data, "error");
    } finally {
      setLoadingStates((prev) => ({ ...prev, [type]: false }));
    }
  };

  const isPoweredByEnabled =
    getCustomisationValue(pageId, "logoSettings", "loginPoweredBy") === "true";
  const loginPreviewMode =
    getCustomisationValue(pageId, "logoSettings", "loginPoweredByLogoMode") ===
    "dark"
      ? "dark"
      : "light";

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" fontWeight="bold">
          Logo
        </Typography>
        <Typography variant="body2" color="text.secondary">
        The logo displayed on certificates issued to learners upon course completion
        </Typography>
      </Box>
      <Divider />
      {pageId !== "LOGIN" && pageId !== "CERTIFICATES" && (
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            p: 2,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ImageUploader
              type="favicon"
              pageId={pageId}
              label="Favicon"
              description="32 x 32 pixels"
              onImageResized={(file) => handleImageUpload(file, "Favicon")}
              loading={loadingStates.Favicon}
            />
          </Box>
          <Box
            sx={{ width: "100%", height: "200px" }}
            component={"img"}
            src={favicon}
            alt="favicon"
          />
        </Box>
      )}
      {/* Login landscape logo upload */}
      <Box
        sx={{
          border: `1px solid ${gray[300]}`,
          borderRadius: "8px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          p: 2,
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ImageUploader
            type="landscape"
            pageId={pageId}
            label="Landscape logo"
            description="200 x 50 pixels"
            onImageResized={(file) => handleImageUpload(file, "LandscapeLogo")}
            loading={loadingStates.LandscapeLogo}
          />
        </Box>
        {pageId === "LOGIN" && (
          <Box
            sx={{ width: "100%", height: "200px" }}
            component={"img"}
            src={loginLogo}
            alt="landscapeLogo"
          />
        )}
        {pageId === "CERTIFICATES" && (
          <Box
            sx={{
              width: "100%",
              height: "200px",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={certificateLogoPreview}
              alt="certificateLogoPreview"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
        )}
        {pageId !== "LOGIN" && pageId !== "CERTIFICATES" && (
          <Box
            sx={{ width: "100%", height: "200px" }}
            component={"img"}
            src={landscapeLogo}
            alt="landscapeLogo"
          />
        )}
      </Box>
      {isMainPortal && pageId === "LOGIN" && (
        <>
          {/* Login light mode powerd by logo upload */}
          <Box
            sx={{
              border: `1px solid ${gray[300]}`,
              borderRadius: "8px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              p: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <ImageUploader
                type="poweredByLogoLight"
                pageId={pageId}
                label="Powered by logo (light)"
                description="200 x 50 pixels"
                onImageResized={(file) =>
                  handleImageUpload(file, "PoweredByLogoLight")
                }
                loading={loadingStates.PoweredByLogoLight}
              />
            </Box>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={poweredByLogo}
              alt="lightModeLogo"
            />
          </Box>
          {/* Login dark mode powerd by logo upload */}
          <Box
            sx={{
              border: `1px solid ${gray[300]}`,
              borderRadius: "8px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              p: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <ImageUploader
                type="poweredByLogoDark"
                pageId={pageId}
                label="Powered by logo (dark)"
                description="200 x 50 pixels"
                onImageResized={(file) =>
                  handleImageUpload(file, "PoweredByLogoDark")
                }
                loading={loadingStates.PoweredByLogoDark}
              />
            </Box>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={powerdByLogoDark}
              alt="darkModeLogo"
            />
          </Box>
        </>
      )}
      {pageId !== "LOGIN" && pageId !== "CERTIFICATES" && (
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            p: 2,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ImageUploader
              type="square"
              pageId={pageId}
              label="Square logo"
              description="128 x 128 pixels"
              onImageResized={(file) => handleImageUpload(file, "SquareLogo")}
              loading={loadingStates.SquareLogo}
            />
          </Box>
          <Box
            sx={{ width: "100%", height: "200px" }}
            component={"img"}
            src={squareLogo}
            alt="squareLogo"
          />
        </Box>
      )}
      {pageId === "LOGIN" && (
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            p: 2,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ pr: { md: 1 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  Powered by
                </Typography>
                <Switch
                  checked={
                    getCustomisationValue(
                      pageId,
                      "logoSettings",
                      "loginPoweredBy",
                    ) === "true"
                  }
                  onChange={(e) =>
                    handleAttributeChange(
                      "loginPoweredBy",
                      e.target.checked ? "true" : "false",
                    )
                  }
                />
              </Box>

              {isPoweredByEnabled && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "1fr 1fr",
                      },
                      gap: 1.5,
                    }}
                  >
                    {modeOptions.map((mode) => {
                      const active = loginPreviewMode === mode.key;
                      return (
                        <Box
                          key={mode.key}
                          onClick={() =>
                            handleAttributeChange(
                              "loginPoweredByLogoMode",
                              mode.key,
                            )
                          }
                          sx={{
                            border: active
                              ? `2px solid ${brand[500]}`
                              : `1px solid ${gray[300]}`,
                            borderRadius: "16px",
                            p: 1.5,
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            backgroundColor: active ? brand[50] : "#fff",
                            "&:hover": {
                              borderColor: active ? brand[500] : gray[400],
                              boxShadow: "0 8px 20px rgba(16, 24, 40, 0.06)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 30,
                                height: 22,
                                borderRadius: "6px",
                                backgroundColor: mode.iconBg,
                                color: mode.iconColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                border:
                                  mode.key === "light"
                                    ? `1px solid ${gray[300]}`
                                    : "1px solid transparent",
                              }}
                            >
                              {mode.iconText}
                            </Box>
                            {active ? (
                              <CheckCircleRoundedIcon
                                sx={{ color: brand[500], fontSize: 20 }}
                              />
                            ) : (
                              <RadioButtonUncheckedRoundedIcon
                                sx={{ color: gray[300], fontSize: 20 }}
                              />
                            )}
                          </Box>
                          <Typography
                            mt={1.5}
                            fontWeight={700}
                            color={gray[800]}
                          >
                            {mode.title}
                          </Typography>
                          <Typography variant="body2" color={gray[600]}>
                            {mode.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}
            </Box>
          </Box>
          <Box
            sx={{ width: "100%", height: "200px" }}
            component={"img"}
            src={loginPreviewMode === "dark" ? powerdByLogoDark : poweredByLogo}
            alt="powered-by"
          />
        </Box>
      )}
    </Stack>
  );
};
